import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

const RequestBetaAccessForm = ({ logo }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState('role-selection');

  useSEO({
    title: 'Get Started',
    description: 'Get immediate access to Veydra. Choose your role and start building simulation models today.'
  });

  useEffect(() => {
    
    // Check if URL has role parameter
    const roleParam = searchParams.get('role');
    if (roleParam && ['educator', 'executive', 'practitioner', 'student', 'other'].includes(roleParam)) {
      setCurrentPage('action-page');
    }
  }, [searchParams]);

  const handleRoleSelection = (role) => {
    if (role) {
      // Navigate to registration in current window when user clicks a role button
      window.location.href = `https://app.veydra.io/register?url=%2Fgetting-started&role=${role}`;
    } else {
      // Skip was clicked - go to action page without role
      setCurrentPage('action-page');
      setSearchParams({});
    }
  };

  // Role selection page (page 1)
  if (currentPage === 'role-selection') {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* Logo */}
        <div className="flex justify-center pt-8 pb-4">
          <Link to="/">
            <img src={logo || "/logo.svg"} className="h-8 w-8 md:h-10 md:w-10" alt="Veydra logo" />
          </Link>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Get Immediate Access to Veydra
            </h1>
            
            <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
              Choose your role for immediate access:
            </p>

            {/* Role Selection Buttons */}
            <div className="space-y-4 max-w-2xl mx-auto mb-8">
              <button 
                onClick={() => handleRoleSelection('executive')}
                className="w-full p-6 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition duration-200"
              >
                <span className="text-2xl font-semibold text-gray-800">Executive</span>
              </button>
              
              <button 
                onClick={() => handleRoleSelection('practitioner')}
                className="w-full p-6 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition duration-200"
              >
                <span className="text-2xl font-semibold text-gray-800">Practitioner</span>
              </button>
              
              <button 
                onClick={() => handleRoleSelection('educator')}
                className="w-full p-6 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition duration-200"
              >
                <span className="text-2xl font-semibold text-gray-800">Educator</span>
              </button>
              
              <button 
                onClick={() => handleRoleSelection('student')}
                className="w-full p-6 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition duration-200"
              >
                <span className="text-2xl font-semibold text-gray-800">Student</span>
              </button>

              <button 
                onClick={() => handleRoleSelection('other')}
                className="w-full p-6 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition duration-200"
              >
                <span className="text-2xl font-semibold text-gray-800">Other</span>
              </button>

              <div className="text-center mt-8 space-x-6">
                <Link 
                  to="/"
                  className="text-gray-500 underline text-sm hover:text-gray-700"
                >
                  Learn more
                </Link>
                <Link 
                  to="/contact"
                  className="text-gray-500 underline text-sm hover:text-gray-700"
                >
                  Contact us
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-200 py-8 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-sm text-gray-500">
              Copyright © 2026 Veydra. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  // Action page (page 2)

  // Action page (page 2)
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Logo */}
      <div className="flex justify-center pt-8 pb-4">
        <Link to="/">
          <img src={logo || "/logo.svg"} className="h-8 w-8 md:h-10 md:w-10" alt="Veydra logo" />
        </Link>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-3xl mx-auto text-center">
          {/* Hero */}
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Decision Intelligence in Action
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            Systems Thinking and AI-Assisted Simulation
          </p>

          <p className="text-lg text-gray-700 mb-8 font-medium">
            Access the Strategic Decision Lab:
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a 
              href={`https://app.veydra.io/register?url=%2Fgetting-started${searchParams.get('role') ? `&role=${searchParams.get('role')}` : ''}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <button className="bg-black text-white py-4 px-8 rounded-lg font-semibold hover:bg-gray-800 transition-colors text-lg w-full sm:w-auto min-w-[200px]">
                Register & Try It Now
              </button>
            </a>
            
            <a 
              href="/contact"
              className="inline-block"
            >
              <button className="border-2 border-gray-300 text-gray-700 py-4 px-8 rounded-lg font-semibold hover:border-gray-400 hover:bg-gray-50 transition-colors text-lg w-full sm:w-auto min-w-[200px]">
                Schedule a Demo
              </button>
            </a>
          </div>

          {/* Contact Link */}
          <div className="text-center mb-32">
            <Link 
              to="/contact"
              className="text-gray-600 underline hover:text-gray-800 transition-colors"
            >
              Contact Us
            </Link>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RequestBetaAccessForm;